/**
 * Region access maps + role/region access helpers.
 *
 * Single source of truth for which regions a user can see/manage —
 * previously duplicated in eip-server/utilities/regionVisibility.ts and
 * eip-client/src/utilities/regionVisibility.ts (both now re-export from here).
 *
 * Region-conditional powers (cross-region visibility bypass) live in
 * getEffectiveVisibleRegions; role→capability questions stay in permissions.ts.
 */

import { Region } from "./common.types";
import { UserRole } from "./user.types";

/**
 * Maps each region to the set of regions its admins can see/manage.
 * superAdmin bypasses this entirely (sees all) via getEffectiveVisibleRegions.
 */
export const REGION_VISIBILITY: Record<Region, Region[]> = {
  // Narrowed 2026-07: CT admins see their own region only — cross-region
  // view/process is superAdmin / CT-auditor territory (visibleRegions: null).
  communitech:  ["communitech"],
  investOttawa: ["investOttawa", "launchLab", "sparkCenter"],
  northForge:   ["northForge"],
  launchLab:    ["launchLab"],
  sparkCenter:  ["sparkCenter"],
};

/**
 * Maps each region to the set of regions it can reassign applications to.
 * Reassignment only allowed at "submitted" application stage.
 * Intentionally independent of REGION_VISIBILITY (CT can route submitted
 * applications anywhere even when its own visibility is narrower).
 */
export const REGION_REASSIGNMENT: Record<Region, Region[]> = {
  communitech:  ["communitech", "northForge", "investOttawa", "launchLab", "sparkCenter"],
  investOttawa: ["investOttawa", "launchLab", "sparkCenter"],
  northForge:   [],
  launchLab:    [],
  sparkCenter:  [],
};

/**
 * Maps each region to the regions whose SP firms it can view/assign.
 * MV sub-regions (LL/SC) see their parent curator's (IO) firm list.
 */
export const FIRM_SOURCE_REGIONS: Record<Region, Region[]> = {
  communitech:  ["communitech"],
  investOttawa: ["investOttawa", "launchLab", "sparkCenter"],
  northForge:   ["northForge"],
  launchLab:    ["investOttawa", "launchLab", "sparkCenter"],
  sparkCenter:  ["investOttawa", "launchLab", "sparkCenter"],
};

/**
 * Regions whose SoWs skip the audit gate entirely (approveSow routes them
 * straight to "Approved & Ready for Signatures").
 */
export const AUDIT_EXEMPT_REGIONS: readonly Region[] = ["investOttawa", "launchLab", "sparkCenter"];

export function regionRequiresSowAudit(region: Region): boolean {
  return !AUDIT_EXEMPT_REGIONS.includes(region);
}

/**
 * Static full-portal capability by region. LL/SC are permanently
 * application-only (MV mode); every other region is full-portal-capable,
 * subject to the region's dynamic RegionSettings.fullPortalEnabled toggle
 * (IO's portal-mode switch). Effective full-portal =
 * regionHasFullPortalStatic(region) && fullPortalEnabled !== false.
 */
export const STATIC_MV_ONLY_REGIONS: readonly Region[] = ["launchLab", "sparkCenter"];

export function regionHasFullPortalStatic(region: Region): boolean {
  return !STATIC_MV_ONLY_REGIONS.includes(region);
}

export function getVisibleRegions(region: Region): Region[] {
  return REGION_VISIBILITY[region] || [region];
}

export function getReassignableRegions(region: Region): Region[] {
  return REGION_REASSIGNMENT[region] || [];
}

export function getFirmSourceRegions(region: Region): Region[] {
  return FIRM_SOURCE_REGIONS[region] || [];
}

/**
 * The single source of truth for region-scoped visibility per role+region.
 * null = unrestricted (all regions) — the superAdmin-style bypass, which the
 * communitech auditor shares (full superAdmin equivalence).
 */
export function getEffectiveVisibleRegions(role: UserRole, region: Region): Region[] | null {
  if (role === "superAdmin") return null;
  if (role === "auditor" && region === "communitech") return null;
  return getVisibleRegions(region);
}

/**
 * Regions whose SoWs this user may AUDIT (queue + outcome submission).
 * Deliberately decoupled from general visibility: each region's auditor is
 * legally responsible for their own region's SoW approval process, so even
 * the communitech auditor (whose general visibility is unrestricted) audits
 * communitech SoWs only.
 */
export function getAuditableRegions(role: UserRole, region: Region): Region[] {
  return role === "auditor" ? [region] : [];
}

export type EffectiveAccess = {
  /** null = unrestricted (all regions) */
  visibleRegions: Region[] | null;
  /**
   * View As — environment-dependent (2026-07):
   * PROD: superAdmin + communitech auditor only.
   * Non-prod (stab/staging/seus/local): any admin-level role.
   */
  canImpersonate: boolean;
  /** SoW lifecycle audit gate: auditor only */
  canAudit: boolean;
  /** Regions whose SoWs the user may audit — home region only, never cross-region */
  auditableRegions: Region[];
};

/**
 * View As availability. isProduction defaults to true so every un-migrated
 * caller fails closed to the strict PROD rule: superAdmin + communitech
 * auditor only. Non-prod (stab/staging/seus/local/test) opens View As to all
 * admin-level roles (partnerAdmin, superAdmin, any-region auditor). Targets
 * remain region-scoped via visibleRegions for non-superAdmins regardless of
 * environment.
 */
export function canImpersonate(role: UserRole, region: Region, isProduction: boolean = true): boolean {
  if (role === "superAdmin" || (role === "auditor" && region === "communitech")) return true;
  return !isProduction && isAdminLevelRole(role);
}

/**
 * "View Legacy" lens availability — READ-ONLY visibility into the frozen
 * pre-June-2026 cohort (legacy: true docs). Communitech superAdmin +
 * communitech auditor only; NF auditor excluded. Always evaluated against
 * the REAL authenticated user (the impersonator while View As is active),
 * never the impersonated role. No environment dependency — PROD included
 * ("SEUS/STAB first" is rollout order, not a code branch). Deliberately NOT
 * part of EffectiveAccess: the client computes that from the effective
 * (possibly impersonated) identity, which is the wrong input here — the
 * client receives this entitlement server-computed via /user-info instead.
 */
export function canViewLegacy(role: UserRole, region: Region): boolean {
  return region === "communitech" && (role === "superAdmin" || role === "auditor");
}

export type AccessEnvOptions = { isProduction?: boolean };

/**
 * Role+region access summary. Server middleware/controllers and client
 * components both resolve through this — never inline role comparisons.
 * Omitting opts.isProduction assumes production (fail-closed).
 */
export function getEffectiveAccess(role: UserRole, region: Region, opts?: AccessEnvOptions): EffectiveAccess {
  return {
    visibleRegions: getEffectiveVisibleRegions(role, region),
    canImpersonate: canImpersonate(role, region, opts?.isProduction ?? true),
    canAudit: role === "auditor",
    auditableRegions: getAuditableRegions(role, region),
  };
}

/** Roles that get the admin-level surface (dashboards, settings, member views). */
export const ADMIN_LEVEL_ROLES: readonly UserRole[] = ["partnerAdmin", "superAdmin", "auditor"];

export function isAdminLevelRole(role: UserRole | null | undefined): boolean {
  return !!role && ADMIN_LEVEL_ROLES.includes(role);
}

export function isSuperAdmin(role: UserRole | null | undefined): boolean {
  return role === "superAdmin";
}

/**
 * Global email-template stewardship — may edit the CONTENT of global
 * (region: null) templates and roll back their versions. Communitech operates
 * the shared program defaults, so CT partnerAdmin holds this alongside
 * superAdmin and the CT auditor (whose null visibleRegions already granted it
 * server-side — this predicate closes the client/server drift). Creating,
 * deactivating, and re-activating global templates stays superAdmin-only:
 * those switch the email on/off for every region at once.
 */
export function canEditGlobalEmailTemplates(role: UserRole, region: Region): boolean {
  if (role === "superAdmin") return true;
  return region === "communitech" && (role === "partnerAdmin" || role === "auditor");
}
